import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { setCachedCatalog } from '../lib/catalogCache';
import { useBasket } from '../hooks/useBasket';

// Customer-facing "Specials" shelf. Shows only products on sale FOR THIS
// customer — the server already personalizes onSale/salePrice per customer
// (tier + audience + date window), so we just filter the catalog by onSale.
// Special-order items are excluded (they can't be added to the basket anyway).
export default function Specials() {
  const { addItems } = useBasket();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qty, setQty] = useState({});       // id -> quantity
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Always fetch fresh: a just-published special must show immediately.
        // The 2-min sessionStorage catalog cache would otherwise hide new
        // specials. Refresh the shared cache so other pages get the new prices.
        const catalogData = await api.getCatalog();
        setCachedCatalog(catalogData);
        if (cancelled) return;
        const onSale = (catalogData.products || []).filter(p => p.onSale && !p.specialOrder);
        setProducts(onSale);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setQuantity = (id, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setQty(q => ({ ...q, [id]: n }));
  };

  function addToBasket() {
    const items = products
      .filter(p => (qty[p.id] || 0) > 0 && p.sosId)
      .map(p => ({
        itemId: p.sosId,
        sosId: p.sosId,
        name: p.name,
        description: p.description || '',
        sku: p.sku,
        quantity: qty[p.id],
        price: p.price,
        onSale: true,
        originalPrice: p.originalPrice ?? p.price,
        saleLabel: p.saleLabel || null,
      }));
    if (items.length === 0) return;
    addItems(items);
    setQty({});
    setAdded(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setAdded(false), 4000);
  }

  const selectedCount = Object.values(qty).filter(q => q > 0).length;

  if (loading) return <div style={s.wrap}><p style={s.sub}>Loading specials…</p></div>;
  if (error) return <div style={s.wrap}><p style={s.error}>{error}</p></div>;

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h1 style={s.h1}>🔥 Specials</h1>
        <p style={s.sub}>Limited-time deals on your products. Prices shown already include the discount.</p>
      </div>

      {added && <div style={s.added}>Added to basket.</div>}

      {products.length === 0 ? (
        <div style={s.empty}>
          <p style={s.sub}>No specials right now — check back soon.</p>
        </div>
      ) : (
        <>
          <div style={s.grid}>
            {products.map(p => {
              const q = qty[p.id] || 0;
              const save = (p.originalPrice || 0) - (p.price || 0);
              return (
                <div key={p.id} style={s.card}>
                  <div style={s.imgWrap}>
                    {p.hasImage && p.sku ? (
                      <img
                        src={`/images/products/${p.sku}.jpg`}
                        alt=""
                        loading="lazy"
                        style={s.img}
                        onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    ) : (
                      <span style={s.imgPlaceholder}>☕</span>
                    )}
                    <div style={s.badge}>{p.saleLabel || 'SALE'}</div>
                  </div>
                  <div style={s.cardBody}>
                    <div style={s.name}>{p.name}</div>
                    <div style={s.meta}>{p.sku}{p.brand ? ` · ${p.brand}` : ''}</div>
                    {p.description && <div style={s.desc}>{p.description}</div>}
                    <div style={s.priceRow}>
                      {p.originalPrice > p.price && (
                        <span style={s.was}>${p.originalPrice.toFixed(2)}</span>
                      )}
                      <span style={s.now}>${(p.price || 0).toFixed(2)}</span>
                      {save > 0 && <span style={s.save}>Save ${save.toFixed(2)}</span>}
                    </div>
                    <div style={s.qtyRow}>
                      <button style={s.qtyBtn} onClick={() => setQuantity(p.id, q - 1)}>−</button>
                      <input
                        style={s.qtyInput}
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={q || ''}
                        placeholder="0"
                        onChange={e => setQuantity(p.id, e.target.value)}
                      />
                      <button style={s.qtyBtn} onClick={() => setQuantity(p.id, q + 1)}>+</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={s.footer}>
            <button
              style={{ ...s.addBtn, opacity: selectedCount ? 1 : 0.5 }}
              disabled={!selectedCount}
              onClick={addToBasket}
            >
              Add {selectedCount || ''} to basket
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const RED = '#A50B18';
const s = {
  wrap: { maxWidth: 1280, margin: '0 auto', padding: '1.75rem 1.25rem 5rem' },
  header: { marginBottom: '1.75rem' },
  h1: { fontSize: '2rem', fontWeight: 800, color: '#222', margin: '0 0 0.35rem' },
  sub: { color: '#888', fontSize: '1rem', margin: 0 },
  error: { color: RED, fontSize: '0.9rem' },
  added: { background: '#e6f4ea', color: '#2e7d32', border: '1px solid #b6e0c2', borderRadius: 8, padding: '0.7rem 1.1rem', marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600 },
  empty: { textAlign: 'center', padding: '3rem 1rem' },
  // min(100%, 320px) keeps cards from overflowing narrow phone screens (a bare
  // 320px min forces horizontal scroll on ~320px devices) while still giving
  // multiple roomy columns on wider screens.
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '1.25rem' },
  card: { position: 'relative', border: '1px solid #eee', borderRadius: 16, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  imgWrap: { position: 'relative', width: '100%', height: 210, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #f0f0f0' },
  img: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: '0.75rem', boxSizing: 'border-box' },
  imgPlaceholder: { fontSize: '3.5rem', opacity: 0.25 },
  badge: { position: 'absolute', top: 12, left: 12, background: RED, color: '#fff', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.05em', padding: '0.3rem 0.7rem', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
  cardBody: { padding: '1.1rem 1.2rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 },
  name: { fontSize: '1.2rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25 },
  meta: { fontSize: '0.85rem', color: '#999', fontFamily: 'monospace' },
  desc: { fontSize: '0.9rem', color: '#666', flex: 1, lineHeight: 1.4 },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.5rem' },
  was: { textDecoration: 'line-through', color: 'rgba(0,0,0,0.4)', fontFamily: 'monospace', fontSize: '1.05rem' },
  now: { color: RED, fontWeight: 800, fontFamily: 'monospace', fontSize: '1.7rem' },
  save: { background: '#e6f4ea', color: '#2e7d32', borderRadius: 5, padding: '0.15rem 0.5rem', fontSize: '0.82rem', fontWeight: 700 },
  qtyRow: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.9rem' },
  qtyBtn: { width: 40, height: 40, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '1.3rem', lineHeight: 1, color: '#333' },
  qtyInput: { width: 64, height: 40, textAlign: 'center', borderRadius: 10, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: '1rem', outline: 'none' },
  footer: { position: 'sticky', bottom: 0, marginTop: '1.75rem', display: 'flex', justifyContent: 'flex-end' },
  addBtn: { background: RED, color: '#fff', border: 'none', borderRadius: 12, padding: '0.9rem 1.9rem', fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(165,11,24,0.25)' },
};
